import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { Button, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { FolderOpen, Paperclip } from 'lucide-react';
import type { RefObject } from 'react';

interface ChatAttachmentActionsProps {
  className?: string;
  disabled?: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  supportedFileTypes: readonly string[];
  onAttachmentClick: () => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFolderAttachmentClick?: () => void;
}

/** Shared file and folder picker controls for Nova prompt editors. */
export function ChatAttachmentActions({
  className,
  disabled,
  fileInputRef,
  supportedFileTypes,
  onAttachmentClick,
  onFileUpload,
  onFolderAttachmentClick,
}: ChatAttachmentActionsProps) {
  const isLocalFolderAttachmentsEnabled = useFeatureFlag('nova-local-folder-attachments', false);

  return (
    <div className={cn('flex items-center', className)}>
      <TooltipProvider delayDuration={350}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label="Attach files"
              data-testid="attachment-button"
              variant="transparent"
              disabled={disabled}
              className={cn(
                'text-muted-foreground h-fit rounded-full bg-transparent p-2 transition-colors',
                'hover:text-foreground border-none',
              )}
              onClick={onAttachmentClick}
              type="button"
            >
              <Paperclip size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Attach files</TooltipContent>
        </Tooltip>

        {isLocalFolderAttachmentsEnabled && onFolderAttachmentClick ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Attach folder"
                data-testid="folder-attachment-button"
                variant="transparent"
                disabled={disabled}
                className={cn(
                  'text-muted-foreground h-fit rounded-full bg-transparent p-2 transition-colors',
                  'hover:text-foreground border-none',
                )}
                onClick={onFolderAttachmentClick}
                type="button"
              >
                <FolderOpen size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Attach folder</TooltipContent>
          </Tooltip>
        ) : null}
      </TooltipProvider>

      <input
        ref={fileInputRef}
        data-testid="attachment-file-input"
        type="file"
        accept={supportedFileTypes.join(',')}
        multiple
        onChange={onFileUpload}
        className="hidden"
      />
    </div>
  );
}
